import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import {
  listings,
  categoryGroups,
  cities,
  districtsByCity,
} from "../../mocks/listings";

function formatPrice(price: number) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(price);
}

function HeroSearch() {
  const [selectedCity, setSelectedCity] = useState("");
  const [districts, setDistricts] = useState<string[]>([]);

  useEffect(() => {
    if (selectedCity && districtsByCity[selectedCity]) {
      setDistricts(districtsByCity[selectedCity]);
    } else {
      setDistricts([]);
    }
  }, [selectedCity]);

  return (
    <section className="relative min-h-[580px] md:min-h-[640px] bg-surface-50 overflow-hidden">
      {/* Decorative blob */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full bg-primary-500/[0.04] -translate-y-1/3 translate-x-1/4 pointer-events-none" />

      <div className="relative w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 w-full lg:max-w-[55%]">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-200 bg-white shadow-soft mb-6">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-seedling-line text-sm text-primary-500" />
              </div>
              <span className="text-xs font-medium text-primary-600">
                Türkiye'nin Arsa Platformu
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-surface-900 leading-[1.1] tracking-tight mb-5">
              Arsa Al, Sat,
              <br />
              <span className="italic font-display text-primary-500">
                Yatırım Yap.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg text-surface-500 leading-relaxed mb-8 max-w-lg">
              Hızlıca ilan ver, kolayca arsa bul — binlerce arsa ilanı tek
              platformda.
            </p>

            {/* Search Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-surface-200 shadow-medium p-4 md:p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* City */}
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="text-xs font-medium text-surface-500 mb-1.5 block">
                    Şehir / İlçe
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400">
                      <i className="ri-map-pin-line text-sm" />
                    </div>
                    <select
                      className="w-full pl-9 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                    >
                      <option value="">Tüm Şehirler</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                      <i className="ri-arrow-down-s-line" />
                    </div>
                  </div>
                </div>

                {/* District */}
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="text-xs font-medium text-surface-500 mb-1.5 block">
                    İlçe
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400">
                      <i className="ri-map-pin-2-line text-sm" />
                    </div>
                    <select
                      className="w-full pl-9 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 disabled:opacity-50"
                      disabled={!districts.length}
                    >
                      <option value="">
                        {districts.length
                          ? "İlçe Seçin"
                          : "Önce Şehir Seçin"}
                      </option>
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                      <i className="ri-arrow-down-s-line" />
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="text-xs font-medium text-surface-500 mb-1.5 block">
                    Fiyat Aralığı
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-surface-400">
                        ₺
                      </span>
                      <input
                        type="text"
                        placeholder="Min"
                        className="w-full pl-6 pr-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                      />
                    </div>
                    <span className="text-surface-300 text-sm">-</span>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-surface-400">
                        ₺
                      </span>
                      <input
                        type="text"
                        placeholder="Maks"
                        className="w-full pl-6 pr-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Area + Button */}
                <div className="sm:col-span-2 lg:col-span-1 flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-surface-500 mb-1.5 block">
                      Alan (m²)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Min"
                        className="w-full px-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                      />
                      <span className="text-surface-300 text-sm">-</span>
                      <input
                        type="text"
                        placeholder="Maks"
                        className="w-full px-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                      />
                    </div>
                  </div>
                  <Link
                    to="/ilanlar"
                    className="shrink-0 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors flex items-center gap-2 whitespace-nowrap h-[42px]"
                  >
                    Ara
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="hidden lg:block flex-1 max-w-[45%]">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20green%20rolling%20hills%20with%20small%20plots%20of%20vacant%20land%20scattered%20across%20lush%20meadows%20soft%20golden%20sunlight%20minimal%20clean%20background%20high%20quality%20photography%20horizontal%20landscape&width=600&height=450&seq=100&orientation=landscape"
                alt="Arsa ilanları"
                className="w-full h-[420px] object-cover rounded-2xl"
              />
              {/* Floating stat card */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-medium border border-surface-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-landscape-line text-lg" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-surface-900">
                      2.400+
                    </p>
                    <p className="text-xs text-surface-500">Aktif İlan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-surface-900 mb-2">
                Arsa Kategorileri
              </h2>
              <p className="text-sm md:text-base text-surface-500 max-w-md">
                Aradığınız arsa türüne göre hızlıca ilanları keşfedin.
              </p>
            </div>
            <Link
              to="/ilanlar"
              className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1.5 shrink-0"
            >
              Tüm İlanları Gör
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {categoryGroups.map((cat) => (
              <Link
                key={cat.id}
                to="/ilanlar"
                className={`group relative rounded-2xl ${cat.bgColor} p-6 md:p-8 transition-all hover:shadow-medium border border-transparent hover:border-surface-200`}
              >
                <div className="flex flex-col h-full">
                  <div
                    className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white shadow-card mb-5`}
                  >
                    <div className={`w-6 h-6 flex items-center justify-center ${cat.textColor}`}>
                      <i className={`${cat.icon} text-xl`} />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-surface-900 mb-2">
                    {cat.title}
                  </h3>
                  <p className="text-sm text-surface-500 leading-relaxed mb-4 flex-1">
                    {cat.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-surface-700">
                      {cat.count} ilan
                    </span>
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-card text-surface-400 group-hover:text-primary-500 transition-colors`}
                    >
                      <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedListings() {
  const featured = listings.filter((l) => l.featured).slice(0, 6);

  return (
    <section className="bg-surface-50 py-16 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-surface-900 mb-2">
                Öne Çıkan İlanlar
              </h2>
              <p className="text-sm md:text-base text-surface-500 max-w-md">
                Editörlerimiz tarafından seçilmiş, yatırım değeri yüksek
                ilanlar.
              </p>
            </div>
            <Link
              to="/ilanlar"
              className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1.5 shrink-0"
            >
              Tümünü Gör
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {featured.map((listing) => (
              <Link
                key={listing.id}
                to={`/ilan/${listing.id}`}
                className="group bg-white rounded-2xl overflow-hidden border border-surface-100 hover:border-surface-200 hover:shadow-medium transition-all"
              >
                <div className="relative h-48 md:h-52 overflow-hidden">
                  <img
                    src={listing.photos[0]}
                    alt={listing.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    {listing.user_type === "Emlak Ofisi" && (
                      <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-primary-600 rounded-full">
                        Emlak Ofisi
                      </span>
                    )}
                    {listing.swap && (
                      <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-accent-600 rounded-full">
                        Takas Olur
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-surface-900/80 backdrop-blur-sm text-white text-xs font-medium rounded-lg">
                    {listing.photos.length} foto
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 text-xs text-surface-500 mb-2">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-pin-line" />
                    </div>
                    {listing.city}, {listing.district}
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-surface-900 mb-3 line-clamp-2 leading-snug">
                    {listing.title}
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-surface-500 mb-4">
                    <span className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-shape-line" />
                      </div>
                      {listing.area_m2.toLocaleString("tr-TR")} m²
                    </span>
                    <span className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-file-list-line" />
                      </div>
                      {listing.zoning_status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-surface-100">
                    <span className="text-lg font-bold text-primary-600">
                      {formatPrice(listing.price)}
                    </span>
                    <span className="text-xs text-surface-400">
                      {listing.user_name}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: "ri-search-line",
      title: "Ara ve Keşfet",
      desc: "Şehir, fiyat ve alan filtresiyle binlerce arsa ilanını saniyeler içinde keşfedin.",
    },
    {
      icon: "ri-file-add-line",
      title: "Hızlı İlan Ver",
      desc: "Tek sayfalık form ile dakikalar içinde ilanınızı yayınlayın. EİDS doğrulaması dahil.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Güvenli İletişim",
      desc: "WhatsApp üzerinden doğrudan ilan sahibiyle iletişime geçin. Güvenli ve hızlı.",
    },
  ];

  return (
    <section id="nasil-calisir" className="bg-white py-16 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-surface-900 mb-3">
              Nasıl Çalışır?
            </h2>
            <p className="text-sm md:text-base text-surface-500 max-w-md mx-auto">
              Üç basit adımda arsanızı bulun veya ilanınızı verin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((step, idx) => (
              <div key={step.title} className="relative text-center">
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-surface-200" />
                )}
                <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500 mx-auto mb-5">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                </div>
                <span className="inline-block px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full mb-3">
                  {idx + 1}
                </span>
                <h3 className="text-base font-bold text-surface-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-surface-500 leading-relaxed max-w-xs mx-auto">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="bg-primary-500 py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Arsanızı Hemen Satmaya Başlayın
            </h2>
            <p className="text-sm md:text-base text-primary-100 max-w-lg">
              Ücretsiz ilan verin, binlerce potansiyel alıcıya ulaşın. Dakikalar
              içinde ilanınız yayında.
            </p>
          </div>
          <Link
            to="/ilan-ver"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-primary-600 text-sm font-bold rounded-full hover:bg-primary-50 transition-colors shadow-soft whitespace-nowrap shrink-0"
          >
            Ücretsiz İlan Ver
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  const stats = [
    { value: "2.400+", label: "Aktif İlan" },
    { value: "850+", label: "Emlak Ofisi" },
    { value: "12.000+", label: "Mutlu Kullanıcı" },
    { value: "81", label: "Şehir" },
  ];

  return (
    <section className="bg-surface-50 border-y border-surface-200 py-8 md:py-10">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-surface-900 mb-1">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-surface-500">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSearch />
        <StatsBar />
        <CategorySection />
        <FeaturedListings />
        <HowItWorks />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}